import type { OrderLineItem, Resolution, ResolutionOption, Shop } from "../types";
import {
  hasAlternativeVariants,
  type VariantCollection,
} from "./variant-availability.lib";

export type { VariantCollection };

export type OrderOpt = {
  orderCanBeRefunded?: boolean;
  orderCanBeExchanged?: boolean;
  orderCanBeExchangedWithCredit?: boolean;
  orderCanBeDiscounted?: boolean;
  optByLineItemId?: Record<
    string,
    {
      productCanBeRefund?: boolean;
      productCanBeExchanged?: boolean;
      productCanBeExchangedWithCredit?: boolean;
      productCanBeDiscounted?: boolean;
    }
  >;
};

type ShopExchange = {
  activated?: boolean;
  available?: boolean;
  minimalStock?: number;
  exchangeWithOtherArticles?: boolean;
};
type ShopDiscount = { activated?: boolean; available?: boolean };
type ShopPreferences = { nonReturnableItems?: string[] };
type ShopForResolutions = Shop & {
  exchange?: ShopExchange;
  discount?: ShopDiscount;
  preferences?: ShopPreferences;
};
type LineItemForResolutions = OrderLineItem & {
  product_id?: string;
  line_item_id?: string;
  variant?: { variant_id?: string | number };
};

/**
 * True when a shop tag (`refund.tagsForNotAcceptedRefund`) on the order or on
 * the product removes the refund for this line item.
 */
export function isRefundBlockedByTag(
  opt: OrderOpt | null | undefined,
  lineItemId: string,
): boolean {
  return (
    opt?.orderCanBeRefunded === false ||
    opt?.optByLineItemId?.[lineItemId]?.productCanBeRefund === false
  );
}

export type ResolutionAccess = {
  /** A merchant may refund items the shop tags as not refundable. */
  isMerchant?: boolean;
};

type ResolutionContext = {
  shop: ShopForResolutions;
  lineItem: LineItemForResolutions;
  variantCollection: VariantCollection | null | undefined;
  opt: OrderOpt | null | undefined;
  lineOpt: NonNullable<OrderOpt["optByLineItemId"]>[string] | undefined;
  lineItemId: string;
  isMerchant: boolean;
};

const isExchangeEnabled = ({ shop }: ResolutionContext) =>
  shop.exchange?.activated === true && shop.exchange?.available === true;

const hasSwapVariant = ({ shop, lineItem, variantCollection }: ResolutionContext) =>
  !variantCollection ||
  (variantCollection.products.length > 0 &&
    hasAlternativeVariants(
      variantCollection,
      shop.exchange?.minimalStock ?? 0,
      String(lineItem.variant?.variant_id ?? ""),
    ));

// Evaluated in order: the order of this table is the order of the options.
const RESOLUTION_RULES: [Resolution, (ctx: ResolutionContext) => boolean][] = [
  ["refund", (ctx) => ctx.isMerchant || !isRefundBlockedByTag(ctx.opt, ctx.lineItemId)],
  [
    "exchange",
    (ctx) =>
      isExchangeEnabled(ctx) &&
      ctx.opt?.orderCanBeExchanged !== false &&
      ctx.lineOpt?.productCanBeExchanged !== false &&
      hasSwapVariant(ctx),
  ],
  [
    "discount",
    ({ shop, opt, lineOpt }) =>
      shop.discount?.activated === true &&
      shop.discount?.available === true &&
      opt?.orderCanBeDiscounted !== false &&
      lineOpt?.productCanBeDiscounted !== false,
  ],
  [
    "exchange-credit",
    (ctx) =>
      isExchangeEnabled(ctx) &&
      ctx.shop.exchange?.exchangeWithOtherArticles === true &&
      ctx.opt?.orderCanBeExchangedWithCredit !== false &&
      ctx.lineOpt?.productCanBeExchangedWithCredit !== false,
  ],
];

export function getAvailableResolutions(
  shop: Shop,
  lineItem: OrderLineItem,
  variantCollection?: VariantCollection | null,
  opt?: OrderOpt | null,
  { isMerchant = false }: ResolutionAccess = {},
): Resolution[] {
  const shopTyped = shop as ShopForResolutions;
  const lineItemTyped = lineItem as LineItemForResolutions;
  const nonReturnable = shopTyped.preferences?.nonReturnableItems ?? [];
  if (lineItemTyped.product_id && nonReturnable.includes(lineItemTyped.product_id)) {
    return [];
  }

  const lineItemId = lineItemTyped.line_item_id ?? "";
  const ctx: ResolutionContext = {
    shop: shopTyped,
    lineItem: lineItemTyped,
    variantCollection,
    opt,
    lineOpt: opt?.optByLineItemId?.[lineItemId],
    lineItemId,
    isMerchant,
  };
  return RESOLUTION_RULES.filter(([, isAllowed]) => isAllowed(ctx)).map(
    ([resolution]) => resolution,
  );
}

export type SingleFamilyLeaf = {
  resolution: Resolution;
  resolutionOption: ResolutionOption | null;
} | null;

export type LeafOptions = {
  /**
   * Catalog mode (merchant previewing the widget outside the storefront):
   * exchange-credit then requires staging articles in the catalog picker, so
   * it needs its own step and cannot be committed straight away.
   */
  catalogSearchActive?: boolean;
};

/**
 * Returns the sole leaf for a family if it has exactly one available
 * sub-option that requires no further input (i.e. everything except
 * variant-swap exchange, which always needs a manual variant pick).
 * Returns null if the family has 0 or 2+ options, or its only option
 * needs a variant pick.
 */
export function getAutoCommittableLeaf(
  family: "refund" | "exchange",
  rawResolutions: Resolution[],
  options: LeafOptions = {},
): SingleFamilyLeaf {
  if (family === "refund") {
    const hasBankTransfer = rawResolutions.includes("refund");
    const hasDiscount = rawResolutions.includes("discount");
    if (hasBankTransfer && !hasDiscount) {
      return { resolution: "refund", resolutionOption: "bank-transfer" };
    }
    if (hasDiscount && !hasBankTransfer) {
      return { resolution: "discount", resolutionOption: "discount-code" };
    }
    return null;
  }

  const hasVariant = rawResolutions.includes("exchange");
  const hasCredit = rawResolutions.includes("exchange-credit");
  if (hasCredit && !hasVariant && !options.catalogSearchActive) {
    return { resolution: "exchange-credit", resolutionOption: null };
  }
  return null;
}
